import { useCallback, useMemo, useState } from 'react';
import type { MergePair } from '../types';
import type { DecoratedMergePair, MergeNoticeState } from './types';
import {
  addCleanupHistoryEntry,
  removeCleanupHistoryEntry,
} from '../../../../../hooks/useCleanupHistory';

const MERGED_STORAGE_KEY = 'novala.mergedPairs';
const DISMISSED_STORAGE_KEY = 'novala.dismissedPairs';
const SWAPPED_STORAGE_KEY = 'novala.swappedPairs';

function loadIds(key: string): Set<string> {
  try {
    const raw = window.localStorage.getItem(key);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function saveIds(key: string, ids: Set<string>): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(Array.from(ids)));
  } catch {}
}

function effectiveSurvivor(original: 'A' | 'B', isSwapped: boolean): 'A' | 'B' {
  if (!isSwapped) return original;
  return original === 'A' ? 'B' : 'A';
}

interface UseMergeActionsResult {
  decoratedPairs: DecoratedMergePair[];
  notice: MergeNoticeState | null;
  merge: (id: string) => void;
  dismiss: (id: string) => void;
  swap: (id: string) => void;
  undo: () => void;
  mergeAll: () => void;
  dismissAll: () => void;
}

export function useMergeActions(pairs: MergePair[]): UseMergeActionsResult {
  const [mergedIds, setMergedIds] = useState<Set<string>>(() => loadIds(MERGED_STORAGE_KEY));
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => loadIds(DISMISSED_STORAGE_KEY));
  const [swappedIds, setSwappedIds] = useState<Set<string>>(() => loadIds(SWAPPED_STORAGE_KEY));
  const [notice, setNotice] = useState<MergeNoticeState | null>(null);

  const decoratedPairs = useMemo<DecoratedMergePair[]>(() => {
    return pairs
      .filter((p) => !mergedIds.has(p.id) && !dismissedIds.has(p.id))
      .map((p) => ({
        ...p,
        survivor: effectiveSurvivor(p.survivor, swappedIds.has(p.id)),
        state: 'pending' as const,
      }));
  }, [pairs, mergedIds, dismissedIds, swappedIds]);

  const merge = useCallback(
    (id: string) => {
      const pair = pairs.find((p) => p.id === id);
      if (!pair) return;
      const isSwapped = swappedIds.has(id);
      const sur = effectiveSurvivor(pair.survivor, isSwapped);
      const keeper = sur === 'A' ? pair.accountA : pair.accountB;
      const loser = sur === 'A' ? pair.accountB : pair.accountA;
      const historyId = `h-${Date.now()}`;
      addCleanupHistoryEntry({
        id: historyId,
        dateChanged: new Date().toISOString(),
        job: 'merge',
        jobLabel: 'Merge duplicates',
        jobIcon: 'M',
        approvedBy: 'Claire Kemanzi',
        approvedByVela: false,
        description: `Merged "${loser.name}" into "${keeper.name}"${isSwapped ? ' (direction swapped)' : ''}`,
        canUndo: true,
        undoPayload: { kind: 'merge', pairIds: [id] },
      });
      const next = new Set(mergedIds);
      next.add(id);
      setMergedIds(next);
      saveIds(MERGED_STORAGE_KEY, next);
      setNotice({
        message: `${loser.name} merged into ${keeper.name}.`,
        action: 'merge',
        pairIds: [id],
        historyIds: [historyId],
      });
    },
    [pairs, mergedIds, swappedIds]
  );

  const dismiss = useCallback(
    (id: string) => {
      const pair = pairs.find((p) => p.id === id);
      if (!pair) return;
      const next = new Set(dismissedIds);
      next.add(id);
      setDismissedIds(next);
      saveIds(DISMISSED_STORAGE_KEY, next);
      setNotice({
        message: `${pair.accountA.name} and ${pair.accountB.name} dismissed.`,
        action: 'dismiss',
        pairIds: [id],
        historyIds: [],
      });
    },
    [pairs, dismissedIds]
  );

  const swap = useCallback(
    (id: string) => {
      const next = new Set(swappedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      setSwappedIds(next);
      saveIds(SWAPPED_STORAGE_KEY, next);
    },
    [swappedIds]
  );

  const undo = useCallback(() => {
    if (!notice) return;
    if (notice.action === 'merge') {
      const next = new Set(mergedIds);
      notice.pairIds.forEach((id) => next.delete(id));
      setMergedIds(next);
      saveIds(MERGED_STORAGE_KEY, next);
      notice.historyIds.forEach((id) => removeCleanupHistoryEntry(id));
    } else {
      const next = new Set(dismissedIds);
      notice.pairIds.forEach((id) => next.delete(id));
      setDismissedIds(next);
      saveIds(DISMISSED_STORAGE_KEY, next);
    }
    setNotice(null);
  }, [notice, mergedIds, dismissedIds]);

  const mergeAll = useCallback(() => {
    const pending = pairs.filter((p) => !mergedIds.has(p.id) && !dismissedIds.has(p.id));
    if (pending.length === 0) return;
    const next = new Set(mergedIds);
    const historyIds: string[] = [];
    pending.forEach((pair, idx) => {
      next.add(pair.id);
      const isSwapped = swappedIds.has(pair.id);
      const sur = effectiveSurvivor(pair.survivor, isSwapped);
      const keeper = sur === 'A' ? pair.accountA : pair.accountB;
      const loser = sur === 'A' ? pair.accountB : pair.accountA;
      const hid = `h-${Date.now()}-${idx}`;
      historyIds.push(hid);
      addCleanupHistoryEntry({
        id: hid,
        dateChanged: new Date().toISOString(),
        job: 'merge',
        jobLabel: 'Merge duplicates',
        jobIcon: 'M',
        approvedBy: 'Claire Kemanzi',
        approvedByVela: false,
        description: `Merged "${loser.name}" into "${keeper.name}"${isSwapped ? ' (direction swapped)' : ''}`,
        canUndo: true,
        undoPayload: { kind: 'merge', pairIds: [pair.id] },
      });
    });
    setMergedIds(next);
    saveIds(MERGED_STORAGE_KEY, next);
    setNotice({
      message: `${pending.length} pair${pending.length !== 1 ? 's' : ''} merged.`,
      action: 'merge',
      pairIds: pending.map((p) => p.id),
      historyIds,
    });
  }, [pairs, mergedIds, dismissedIds, swappedIds]);

  const dismissAll = useCallback(() => {
    const pending = pairs.filter((p) => !mergedIds.has(p.id) && !dismissedIds.has(p.id));
    if (pending.length === 0) return;
    const next = new Set(dismissedIds);
    pending.forEach((p) => next.add(p.id));
    setDismissedIds(next);
    saveIds(DISMISSED_STORAGE_KEY, next);
    setNotice({
      message: `${pending.length} pair${pending.length !== 1 ? 's' : ''} dismissed.`,
      action: 'dismiss',
      pairIds: pending.map((p) => p.id),
      historyIds: [],
    });
  }, [pairs, mergedIds, dismissedIds]);

  return {
    decoratedPairs,
    notice,
    merge,
    dismiss,
    swap,
    undo,
    mergeAll,
    dismissAll,
  };
}